import { useState, useEffect } from 'react';
import { Film, XCircle, ChevronDown, ChevronUp, ArrowRight, BookOpen, MessageCircle, Target, CheckCircle2, Zap } from 'lucide-react';

const STORAGE_KEY = 'bb_welcome_video_dismissed';
const DEEP_DIVE_URL = 'https://docs.google.com/document/d/1n3NdPRsBZApVbFY9T2EFLeq0txed77qD6KC5qQHM4ic/edit?usp=drivesdk';

interface WelcomeVideoBannerProps {
    deepDiveUrl?: string;
}

const steps = [
    {
        icon: <Target size={16} />,
        title: 'Focus',
        desc: 'Your daily workspace — log outreach, track 7 success activities',
        color: 'text-amber-400',
        bg: 'bg-amber-500/10',
    },
    {
        icon: <BookOpen size={16} />,
        title: 'Playbooks',
        desc: 'New content dripped to you step by step',
        color: 'text-indigo-400',
        bg: 'bg-indigo-500/10',
    },
    {
        icon: <CheckCircle2 size={16} />,
        title: 'My Action Plan',
        desc: 'Check tasks off and hit Sync to unlock the next level',
        color: 'text-emerald-400',
        bg: 'bg-emerald-500/10',
    },
    {
        icon: <MessageCircle size={16} />,
        title: 'Coach Chat',
        desc: 'Your lifeline — stuck? Send a message, get help within the hour',
        color: 'text-rose-400',
        bg: 'bg-rose-500/10',
    },
    {
        icon: <Zap size={16} />,
        title: 'Sync Button',
        desc: 'Tap to save your work and update your business database',
        color: 'text-purple-400',
        bg: 'bg-purple-500/10',
    },
];

export const WelcomeVideoBanner = ({ deepDiveUrl = DEEP_DIVE_URL }: WelcomeVideoBannerProps) => {
    const [dismissed, setDismissed] = useState(() => localStorage.getItem(STORAGE_KEY) === 'true');
    const [expanded, setExpanded] = useState(false);
    const [visibleStep, setVisibleStep] = useState(0);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, String(dismissed));
    }, [dismissed]);

    useEffect(() => {
        if (!expanded) {
            setVisibleStep(0);
            return;
        }
        if (visibleStep < steps.length) {
            const timer = setTimeout(() => setVisibleStep(v => v + 1), 600);
            return () => clearTimeout(timer);
        }
    }, [expanded, visibleStep]);

    if (dismissed) {return null;}

    return (
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 border border-slate-700/50 rounded-3xl shadow-lg overflow-hidden transition-all">
            {/* Header / collapsed preview */}
            <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3 min-w-0" onClick={() => setExpanded(!expanded)}>
                    <div className="p-2.5 bg-white/10 backdrop-blur-sm rounded-xl shrink-0">
                        <Film size={18} className="text-indigo-300" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-sm font-bold text-white uppercase tracking-tight">Welcome! Platform Walkthrough</h3>
                        <p className="text-[10px] text-slate-400 font-medium uppercase tracking-widest">Get started in 60 seconds</p>
                    </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    {!expanded ? (
                        <button
                            onClick={() => setExpanded(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-500/20 border border-indigo-400/30 rounded-xl text-indigo-300 text-[10px] font-black uppercase tracking-widest hover:bg-indigo-500/30 transition-all"
                        >
                            <ChevronDown size={12} /> Explore
                        </button>
                    ) : (
                        <button
                            onClick={() => setExpanded(false)}
                            className="p-1.5 text-slate-400 hover:text-white transition-colors"
                        >
                            <ChevronUp size={16} />
                        </button>
                    )}
                    <button
                        onClick={() => setDismissed(true)}
                        className="p-1.5 text-slate-500 hover:text-slate-300 transition-colors"
                        title="Dismiss"
                    >
                        <XCircle size={16} />
                    </button>
                </div>
            </div>

            {/* Expanded animated walkthrough */}
            {expanded && (
                <div className="border-t border-slate-700/50">
                    <div className="p-5 space-y-5">
                        {/* Intro */}
                        <div className={`transition-all duration-500 ${visibleStep >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                            <p className="text-sm text-slate-300 leading-relaxed">
                                Think of this as your <span className="text-white font-bold">business control room</span>. 
                                Everything you need to build your business is right here.
                            </p>
                        </div>

                        {/* Philosophy */}
                        <div className={`transition-all duration-500 ${visibleStep >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                            <div className="p-4 bg-white/5 border border-white/10 rounded-2xl">
                                <p className="text-xs text-indigo-300 font-black uppercase tracking-widest mb-1">The Philosophy</p>
                                <p className="text-sm text-slate-300">
                                    We are not building a unicorn. We are building a{' '}
                                    <span className="text-amber-300 font-bold">lifestyle business</span> — 
                                    profitable, sustainable, funding the life you want to live.
                                </p>
                            </div>
                        </div>

                        {/* 5 Sections */}
                        <div className="space-y-2">
                            <p className={`text-[10px] text-slate-500 font-black uppercase tracking-widest transition-all duration-500 ${visibleStep >= 3 ? 'opacity-100' : 'opacity-0'}`}>
                                The 5 Key Sections
                            </p>
                            {steps.map((step, i) => (
                                <div
                                    key={step.title}
                                    className={`flex items-start gap-3 p-3 rounded-xl border border-white/5 transition-all duration-500 ${
                                        visibleStep > i + 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                                    } ${visibleStep === i + 3 ? 'bg-white/10 border-indigo-400/30' : 'bg-white/[0.03]'}`}
                                >
                                    <div className={`p-2 rounded-lg ${step.bg} ${step.color} shrink-0`}>
                                        {step.icon}
                                    </div>
                                    <div className="min-w-0">
                                        <p className={`text-xs font-bold ${step.color} uppercase tracking-tight`}>{step.title}</p>
                                        <p className="text-[11px] text-slate-400 mt-0.5">{step.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* CTA */}
                        <div className={`transition-all duration-500 ${visibleStep >= steps.length + 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 bg-gradient-to-r from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-2xl">
                                <div>
                                    <p className="text-xs font-bold text-amber-300 uppercase tracking-tight">Ready to start?</p>
                                    <p className="text-[10px] text-slate-400 mt-0.5">Read the next playbook, then head to My Action Plan</p>
                                </div>
                                {deepDiveUrl && (
                                    <a
                                        href={deepDiveUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-400/30 rounded-xl text-amber-300 text-[10px] font-black uppercase tracking-widest hover:bg-amber-500/30 transition-all shrink-0"
                                    >
                                        Deep Dive Guide <ArrowRight size={12} />
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="px-4 py-3 border-t border-slate-700/50 flex items-center justify-between">
                        <p className="text-[9px] text-slate-500 font-medium">
                            📘 Full guide:{' '}
                            <a href={deepDiveUrl || '#'} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline">
                                How to Use the BizBuilder
                            </a>
                        </p>
                        <button
                            onClick={() => setExpanded(false)}
                            className="flex items-center gap-1 text-[10px] text-slate-500 font-bold uppercase tracking-widest hover:text-slate-300 transition-colors"
                        >
                            <ChevronUp size={12} /> Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
